import { Component, isDevMode } from '@angular/core'
import { NgClass } from '@angular/common'
import { fadeInAnimation, fadeOutAnimation, flipAnimation, bounceOutAnimation } from 'angular-animations'
import { Subject, from, merge, takeUntil, debounceTime } from 'rxjs'
import { FrontendCard as Card, Choice, ChoiceType } from '@nx-superprep/backend/firebase'
import { GameplayBaseComponent } from '../gameplay-base'
import { startsWith } from 'lodash-es'
import { isMandarin, log } from '@nx-superprep/utils'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { MatCardModule } from '@angular/material/card'
import { MatGridListModule } from '@angular/material/grid-list'

@Component({
  standalone: true,
  imports: [
    NgClass,
    MatCardModule,
    MatGridListModule,
  ],
  selector: 'gameplay-flip',
  templateUrl: './flip.component.html',
  styleUrls: ['./flip.component.scss'],
  animations: [
    fadeInAnimation(),
    fadeOutAnimation(),
    flipAnimation(),
    bounceOutAnimation({ duration: 400 }),
    trigger('animationQuestion', [
      state('start', style({ backgroundColor: 'orange' })),
      state('end', style({ backgroundColor: 'pink' })),
      transition('start => end', animate(1500)),
      transition('end => start', animate('800ms 0.5s ease-out')),
    ]),
    trigger('animationAnswer', [
      state('right', style({ backgroundColor: 'yellow' })),
      state('wrong', style({ backgroundColor: 'red' })),
    ]),
  ],
})
export class FlipComponent extends GameplayBaseComponent {
  override title = 'flip'

  private _isFlipped = false
  private readonly onFlip = new Subject<void>()

  /**
   * Getter for `isFlipped` property.
   */
  public get isFlipped(): boolean {
    return this._isFlipped
  }

  /**
   * Setter for `isFlipped` property.
   * @param value - New flip state.
   */
  public set isFlipped(value: boolean) {
    this._isFlipped = value
  }

  /**
   * Toggles the flip state of the card.
   */
  flipCard(): void {
    this.isFlipped = !this.isFlipped
  }

  /**
   * Listens for flip events and processes the choice based on correctness.
   */
  listen(): void {
    this.onCancel.next()
    from(this.onFlip).pipe(
      takeUntil(merge(this.onDestroy, this.onCancel)),
      debounceTime(300)
    ).subscribe(() => {
      const choice = new Choice({
        type: this.isCorrect() ? ChoiceType.answer : ChoiceType.wrong,
        name: this.isCorrect() ? 'Correct' : 'Wrong',
      })
      this.onSelect(choice)
    })
  }

  /**
   * Initializes the component's state with the provided card.
   * @param card - The card used to initialize the component.
   * @returns `true` if the initialization was successful, otherwise `false`.
   */
  override initState(card: Card): boolean {
    if (!super.initState(card)) {
      return false
    }
    this.listen()
    this.isFlipped = false;  // Ensure the card is unflipped at the start
    return true
  }

  /**
   * Flips the card and triggers the flip event if the card is flipped.
   */
  async onFlipCard(): Promise<void> {
    this.flipCard()
    if (this.isFlipped) {
      await this.speak(this.answerText())
      this.onFlip.next(); // Trigger flip event when flipped
    }
  }

  /**
   * Handles speaking the provided text value.
   * @param value - The text to be spoken.
   * @returns A promise that resolves when the text has been spoken.
   */
  async speak(value: string): Promise<void> {
    if (isDevMode()) {
      log.debug(`Speak: ${value}`); // Log in development mode for debugging
      return Promise.resolve()
    }
    // Add actual implementation for text-to-speech if needed
    return Promise.resolve()
  }

  /**
   * Determines the language code based on the provided value and card moduleId.
   * @param value - The value to check for language.
   * @returns The language code.
   */
  lang(value: string): string {
    if (isMandarin(value)) {
      return 'zh-CN'
    }
    if (!this.card) {
      return 'en-US'
    }
    if (startsWith(this.card.template.moduleId, 'CHIN')) {
      return 'zh-CN'
    }
    if (startsWith(this.card.template.moduleId, 'SPAN')) {
      return 'es-ES'
    }
    if (startsWith(this.card.template.moduleId, 'FREN')) {
      return 'fr-FR'
    }
    switch (this.card.template.language) {
      case 'FR':
        return 'fr-FR'
      case 'EN':
        return 'en-US'
      case 'CH':
        return 'zh-CN'
      default:
        return 'en-US'
    }
  }

  // lifecycle methods
  override onForceAnswer(isCorrect: boolean = Math.random() < 0.5): void {
    if (this.isFlipped) {
      const choice = new Choice({
        type: isCorrect ? ChoiceType.answer : ChoiceType.wrong,
        name: isCorrect ? 'Correct' : 'Wrong',
      })
      this.onSelect(choice)
    }
  }
}
